import { MIXPANEL_EVENTS } from "../../../utils/mixpanelEvents";

/**
 * Single catalog of analytics event names for the patient-channel (`src/`)
 * route. Values reuse the legacy `MIXPANEL_EVENTS` strings where an equivalent
 * event already exists; every other entry is a new event in `CONSTANT_CASE`.
 * Keys are derived from each CTA `data-testid` so the mapping stays auditable.
 */
export var ANALYTICS_EVENTS = {
  ACTION_DELETE_CANCEL: "ACTION_DELETE_CANCEL",
  ACTION_DELETE_CONFIRM: "ACTION_DELETE_CONFIRM",
  ACTION_PICKER_ADD_NOTE: "ACTION_PICKER_ADD_NOTE",
  ACTION_PICKER_BACK: "ACTION_PICKER_BACK",
  ACTION_PICKER_COMPLETE_FORM: "ACTION_PICKER_COMPLETE_FORM",
  ACTION_PICKER_CREATE_TASK: "ACTION_PICKER_CREATE_TASK",
  ACTION_PICKER_GENERATE_COMMUNICATION: "ACTION_PICKER_GENERATE_COMMUNICATION",
  ACTION_PICKER_UPDATE_TAGS: "ACTION_PICKER_UPDATE_TAGS",
  REPORT_VERSION_SWITCHED: "REPORT_VERSION_SWITCHED",
  ADDITIONAL_CONTEXT_RETRY: "ADDITIONAL_CONTEXT_RETRY",
  ADD_PATIENT_OPEN: MIXPANEL_EVENTS.OPEN_ADD_PATIENT,
  ADD_PATIENT_SUBMIT: MIXPANEL_EVENTS.SUBMIT_ADD_PATIENT,
  ADD_PATIENT_CANCEL: "ADD_PATIENT_CANCEL",
  ADD_PATIENT_WORKSPACE: "ADD_PATIENT_WORKSPACE",
  ADD_PATIENT_ADD_FILES: "ADD_PATIENT_ADD_FILES",
  ADD_SCANS_SUBMIT: MIXPANEL_EVENTS.ADD_SCANS_FOR_PATIENT,
  ADD_SCANS_SELECT: "ADD_SCANS_SELECT",
  SCAN_UPLOAD_COMPLETE: "SCAN_UPLOAD_COMPLETE",
  SCAN_UPLOAD_DROPZONE: "SCAN_UPLOAD_DROPZONE",
  SCAN_UPLOAD_CANCEL: "SCAN_UPLOAD_CANCEL",
  SCAN_UPLOAD_DONE: "SCAN_UPLOAD_DONE",
  STUDY_CARD_ADD_MORE: "STUDY_CARD_ADD_MORE",
  ADD_TAG_BUTTON: "ADD_TAG_BUTTON",
  ADD_TAG_OPTION: "ADD_TAG_OPTION",
  ADD_TAG_SEARCH: MIXPANEL_EVENTS.PATIENT_TAG_SEARCHED,
  AIRA_CHAT_CLEAR: MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_CLEAR,
  AIRA_CHAT_INPUT: "AIRA_CHAT_INPUT",
  AIRA_CHAT_MIC: MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SPEAK,
  AIRA_CHAT_SEND: MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SEND_BUTTON,
  AIRA_CONVERSATION: "AIRA_CONVERSATION",
  AIRA_CONVERSATION_OPTIONS: "AIRA_CONVERSATION_OPTIONS",
  AIRA_HISTORY_CLOSE: "AIRA_HISTORY_CLOSE",
  AIRA_HISTORY_OPEN: "AIRA_HISTORY_OPEN",
  AIRA_MODAL_CLOSE: "AIRA_MODAL_CLOSE",
  AIRA_MODAL_MAXIMIZE: "AIRA_MODAL_MAXIMIZE",
  AIRA_MODAL_MINIMIZE: "AIRA_MODAL_MINIMIZE",
  AIRA_NEW_CHAT: "AIRA_NEW_CHAT",
  AIRA_PROMPT: MIXPANEL_EVENTS.ASSISTANT_PROMPT_SELECTED,
  AIRA_RENAME: "AIRA_RENAME",
  AIRA_RENAME_INPUT: "AIRA_RENAME_INPUT",
  AIRA_SUGGESTION: MIXPANEL_EVENTS.ASSISTANT_PROMPT_SELECTED,
  AIRA_TAB_GENERAL: "AIRA_TAB_GENERAL",
  AIRA_TAB_PATIENT: "AIRA_TAB_PATIENT",
  AI_SUMMARY_REGENERATE: "AI_SUMMARY_REGENERATE",
  APPOINTMENT_ADD_TASK: "APPOINTMENT_ADD_TASK",
  APPOINTMENT_SECTION: "APPOINTMENT_SECTION",
  APPOINTMENTS_VIEW_ALL: "APPOINTMENTS_VIEW_ALL",
  ASK_AIRA_INPUT: "ASK_AIRA_INPUT",
  ASK_AIRA_SEND: MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SEND_BUTTON,
  ASSISTANT_MESSAGE_COPY: "ASSISTANT_MESSAGE_COPY",
  ASSISTANT_MESSAGE_REGENERATE: "ASSISTANT_MESSAGE_REGENERATE",
  ASSISTANT_MESSAGE_THUMBS_DOWN: MIXPANEL_EVENTS.ASSISTANT_FEEDBACK,
  ASSISTANT_MESSAGE_THUMBS_UP: MIXPANEL_EVENTS.ASSISTANT_FEEDBACK,
  ATTACHMENT_MENU: "ATTACHMENT_MENU",
  ATTACHMENT_PICK: MIXPANEL_EVENTS.ATTACHMENTS_POPOVER_OPENED,
  ATTACHMENT_REMOVE: "ATTACHMENT_REMOVE",
  BIDIRECTIONAL_SYNC_OPEN: "BIDIRECTIONAL_SYNC_OPEN",
  BIDIRECTIONAL_SYNC_REFRESH: "BIDIRECTIONAL_SYNC_REFRESH",
  BP_HIGH: "BP_HIGH",
  BP_LOW: "BP_LOW",
  CITATION: "CITATION",
  COMPOSER_CANCEL: "COMPOSER_CANCEL",
  COMPOSER_CLOSE: "COMPOSER_CLOSE",
  COMPOSER_CONTENT: "COMPOSER_CONTENT",
  COMPOSER_DISCARD: "COMPOSER_DISCARD",
  COMPOSER_MINIMIZE: "COMPOSER_MINIMIZE",
  COMPOSER_RESTORE: "COMPOSER_RESTORE",
  COMPOSER_SAVE: "COMPOSER_SAVE",
  COMM_TEMPLATE_CANCEL: "COMM_TEMPLATE_CANCEL",
  COMM_TEMPLATE_FILTER: "COMM_TEMPLATE_FILTER",
  COMM_TEMPLATE_NAME: "COMM_TEMPLATE_NAME",
  COMM_TEMPLATE_NEW: "COMM_TEMPLATE_NEW",
  COMM_TEMPLATE_ROW: "COMM_TEMPLATE_ROW",
  COMM_TEMPLATE_SAVE: "COMM_TEMPLATE_SAVE",
  COMM_TEMPLATE_SEARCH: "COMM_TEMPLATE_SEARCH",
  COMM_TEMPLATE_TYPE: "COMM_TEMPLATE_TYPE",
  COMM_TEMPLATE_WORKSPACE: "COMM_TEMPLATE_WORKSPACE",
  CREATE_ENCOUNTER_OPEN: "CREATE_ENCOUNTER_OPEN",
  CREATE_TASK_BACK: "CREATE_TASK_BACK",
  CREATE_TASK_CANCEL: "CREATE_TASK_CANCEL",
  CREATE_TASK_CONTEXT_INPUT: "CREATE_TASK_CONTEXT_INPUT",
  CREATE_TASK_LABEL: "CREATE_TASK_LABEL",
  CREATE_TASK_OWNER: "CREATE_TASK_OWNER",
  CREATE_TASK_SAVE: "CREATE_TASK_SAVE",
  CREATE_TASK_SELECT_PATIENT_RECORD: "CREATE_TASK_SELECT_PATIENT_RECORD",
  CREATE_TASK_TITLE_INPUT: "CREATE_TASK_TITLE_INPUT",
  DECISION_CARD: "DECISION_CARD",
  DISCARD_CONFIRM_DISCARD: "DISCARD_CONFIRM_DISCARD",
  DISCARD_CONFIRM_STAY: "DISCARD_CONFIRM_STAY",
  DOC_ACCORDION: "DOC_ACCORDION",
  ENCOUNTER_ADD_NOTE: "ENCOUNTER_ADD_NOTE",
  ENCOUNTER_DOCUMENT: "ENCOUNTER_DOCUMENT",
  ENCOUNTER_FIELD: "ENCOUNTER_FIELD",
  ENCOUNTER_FLOW_BACK: "ENCOUNTER_FLOW_BACK",
  ENCOUNTER_FLOW_CANCEL: "ENCOUNTER_FLOW_CANCEL",
  ENCOUNTER_FLOW_CLOSE: "ENCOUNTER_FLOW_CLOSE",
  ENCOUNTER_FLOW_REVIEW: MIXPANEL_EVENTS.CREATE_ENCOUNTER_REVIEW_CLICKED,
  EDIT_PATIENT_CANCEL: "EDIT_PATIENT_CANCEL",
  EDIT_PATIENT_SAVE: MIXPANEL_EVENTS.SUBMIT_EDIT_PATIENT,
  ENCOUNTER_FLOW_SUBMIT: MIXPANEL_EVENTS.CREATE_ENCOUNTER_SUBMIT_ATTEMPTED,
  ENCOUNTER_ROW: MIXPANEL_EVENTS.ENCOUNTER_DETAIL_OPENED,
  ENCOUNTERS_VIEW_ALL: "ENCOUNTERS_VIEW_ALL",
  ERROR_VIEW_RETRY: "ERROR_VIEW_RETRY",
  ERROR_VIEW_WORKLIST: "ERROR_VIEW_WORKLIST",
  FIELD: "FIELD",
  FILE: "FILE",
  FILE_REMOVE: "FILE_REMOVE",
  FILTERS_APPLY: "FILTERS_APPLY",
  FILTERS_CLEAR: "FILTERS_CLEAR",
  FORM_ACTION_DELETE: "FORM_ACTION_DELETE",
  FORM_ACTION_OPEN: "FORM_ACTION_OPEN",
  FORM_SELECT: "FORM_SELECT",
  GENERATE_SUGGESTED_ACTIONS: "GENERATE_SUGGESTED_ACTIONS",
  GLUCOSE_HIGH: "GLUCOSE_HIGH",
  GLUCOSE_LOW: "GLUCOSE_LOW",
  GLUCOSE_UNIT: "GLUCOSE_UNIT",
  HISTORY_TAB: "HISTORY_TAB",
  LETTER: "LETTER",
  LETTER_ACTION_DELETE: "LETTER_ACTION_DELETE",
  LETTER_ACTION_GENERATE: "LETTER_ACTION_GENERATE",
  LETTER_ALIGN: "LETTER_ALIGN",
  LETTER_BLOCK_TYPE: "LETTER_BLOCK_TYPE",
  LETTER_BOLD: "LETTER_BOLD",
  LETTER_CLEAR: "LETTER_CLEAR",
  LETTER_COLUMNS: "LETTER_COLUMNS",
  LETTER_FONT_DEC: "LETTER_FONT_DEC",
  LETTER_FONT_INC: "LETTER_FONT_INC",
  LETTER_IMAGE: "LETTER_IMAGE",
  LETTER_ITALIC: "LETTER_ITALIC",
  LETTER_LINK: "LETTER_LINK",
  LETTER_PREVIEW_ATTACH: "LETTER_PREVIEW_ATTACH",
  LETTER_PREVIEW_BACK: "LETTER_PREVIEW_BACK",
  LETTER_PREVIEW_TOGGLE: "LETTER_PREVIEW_TOGGLE",
  LETTER_REDO: "LETTER_REDO",
  LETTER_REPORT_SELECT: MIXPANEL_EVENTS.REPORT_SELECTED_FOR_LETTER,
  LETTER_TEMPLATE_SELECT: MIXPANEL_EVENTS.LETTER_TEMPLATE_SELECTED,
  LETTER_UNDERLINE: "LETTER_UNDERLINE",
  LETTER_UNDO: "LETTER_UNDO",
  LETTER_VARIABLE: "LETTER_VARIABLE",
  LINKED_RECORDS_ADD: "LINKED_RECORDS_ADD",
  LINKED_RECORD_ROW: "LINKED_RECORD_ROW",
  LINK_RECORDS_CANCEL: "LINK_RECORDS_CANCEL",
  LINK_RECORDS_CHECKBOX: "LINK_RECORDS_CHECKBOX",
  LINK_RECORDS_CONFIRM: "LINK_RECORDS_CONFIRM",
  LINK_RECORDS_MODAL_CLOSE: "LINK_RECORDS_MODAL_CLOSE",
  LINK_RECORDS_TAB: "LINK_RECORDS_TAB",
  LIVE_TOOL_CALLS_EVENT: "LIVE_TOOL_CALLS_EVENT",
  LIVE_TOOL_CALLS_TOGGLE: "LIVE_TOOL_CALLS_TOGGLE",
  LKW_CALC_CANCEL: MIXPANEL_EVENTS.LKW_CALCULATOR_CANCELLED,
  LKW_CALC_OPEN: MIXPANEL_EVENTS.LKW_CALCULATOR_OPEN,
  LKW_CALC_SAVE: MIXPANEL_EVENTS.LKW_CALCULATOR_SAVED,
  MAINTENANCE_REFRESH: "MAINTENANCE_REFRESH",
  MEDICAL_REPORT_CLOSE: MIXPANEL_EVENTS.MEDICAL_REPORT_MODAL_CLOSED,
  MEDICAL_REPORT_COPY: MIXPANEL_EVENTS.MEDICAL_REPORT_COPIED,
  MEDICAL_REPORT_PRINT: MIXPANEL_EVENTS.MEDICAL_REPORT_PRINTED,
  MERGE_ACKNOWLEDGE: "MERGE_ACKNOWLEDGE",
  MERGE_CANCEL: MIXPANEL_EVENTS.MERGE_REVIEW_CANCELLED,
  MERGE_CONFIRM: MIXPANEL_EVENTS.PATIENT_MERGE,
  MERGE_CONFLICT: "MERGE_CONFLICT",
  MERGE_GO_TO_CURRENT: "MERGE_GO_TO_CURRENT",
  MERGE_REFRESH: "MERGE_REFRESH",
  MERGE_REVIEW_CTA: MIXPANEL_EVENTS.MERGE_REVIEW_OPENED,
  MERGE_UNIQUE: "MERGE_UNIQUE",
  MESSAGE_TOOL_CALLS_TOGGLE: "MESSAGE_TOOL_CALLS_TOGGLE",
  MESSAGE_TOOL_CALL_ROW: "MESSAGE_TOOL_CALL_ROW",
  META_PILL_COPY: "META_PILL_COPY",
  MISSING_FIELDS_CONTEXT_INPUT: "MISSING_FIELDS_CONTEXT_INPUT",
  MISSING_FIELDS_SAVE: "MISSING_FIELDS_SAVE",
  MISSING_FIELDS_TOGGLE: "MISSING_FIELDS_TOGGLE",
  MRN_REFRESH: "MRN_REFRESH",
  MULTI_SELECT_OPTION: "MULTI_SELECT_OPTION",
  MULTI_SELECT_TRIGGER: "MULTI_SELECT_TRIGGER",
  NIHSS: "NIHSS",
  NIHSS_SCORE: "NIHSS_SCORE",
  NIHSS_SCORE_TOGGLE: "NIHSS_SCORE_TOGGLE",
  NOTE_DELETE_CANCEL: "NOTE_DELETE_CANCEL",
  NOTE_DELETE_CONFIRM: "NOTE_DELETE_CONFIRM",
  NOTE_SELECT: "NOTE_SELECT",
  NOTE_TEXT: "NOTE_TEXT",
  PAGE_HEADER_NEXT_PATIENT: MIXPANEL_EVENTS.PATIENT_NAVIGATION,
  PAGE_HEADER_EDIT_PATIENT: MIXPANEL_EVENTS.PATIENT_NAME_EDIT_OPENED,
  PAGE_HEADER_NEXT_PATIENT_NAMED: MIXPANEL_EVENTS.PATIENT_NAVIGATION,
  PAGE_HEADER_PREV_PATIENT: MIXPANEL_EVENTS.PATIENT_NAVIGATION,
  PAGE_HEADER_SHARE_PATIENT: MIXPANEL_EVENTS.PATIENT_SHARE_OPEN,
  PATIENT_DETAIL_COPY: "PATIENT_DETAIL_COPY",
  PATIENT_DETAILS_VIEW_ALL: "PATIENT_DETAILS_VIEW_ALL",
  PATIENT_INFO_SYNC: "PATIENT_INFO_SYNC",
  PATIENT_NOTE_ADD_OPEN: "PATIENT_NOTE_ADD_OPEN",
  PATIENT_NOTE_CREATE: "PATIENT_NOTE_CREATE",
  PATIENT_NOTE_LOAD_MORE: "PATIENT_NOTE_LOAD_MORE",
  PATIENT_NOTE_UPDATE: "PATIENT_NOTE_UPDATE",
  PATIENT_NOTE_DELETE: "PATIENT_NOTE_DELETE",
  PATIENT_SUMMARY_TIMELINE_TOGGLE: "PATIENT_SUMMARY_TIMELINE_TOGGLE",
  PATIENT_TAGS_APPLY: MIXPANEL_EVENTS.PATIENT_TAGS_SAVED,
  PATIENT_TAGS_DISCARD: MIXPANEL_EVENTS.PATIENT_TAGS_DISCARDED,
  PATIENT_TAGS_TOGGLE: MIXPANEL_EVENTS.PATIENT_TAGS_PANEL_TOGGLED,
  PATIENT_TASKS_ADD: "PATIENT_TASKS_ADD",
  PROFILE_IDENTIFIERS_VIEW_MORE: "PROFILE_IDENTIFIERS_VIEW_MORE",
  PROFILE_MENU_LOGOUT: MIXPANEL_EVENTS.USER_LOGGEDOUT,
  PROFILE_MENU_LOGOUT_CANCEL: "PROFILE_MENU_LOGOUT_CANCEL",
  PROFILE_MENU_TERMS: MIXPANEL_EVENTS.OPEN_TERMS_OF_USE,
  PROFILE_MENU_TRIGGER: "PROFILE_MENU_TRIGGER",
  RECORDS_FULL_SYNC: "RECORDS_FULL_SYNC",
  RECORDS_SYNC: "RECORDS_SYNC",
  RECORDS_TAB: "RECORDS_TAB",
  REPORT_AI_TAG: "REPORT_AI_TAG",
  REPORT_AI_TAG_APPLIED: "REPORT_AI_TAG_APPLIED",
  REPORT_DICOM_OPEN: MIXPANEL_EVENTS.RADIOLOGY_REPORT_STUDY_OPENED,
  REPORT_ITEM_TOGGLE: "REPORT_ITEM_TOGGLE",
  REPORT_LINK_STUDY: MIXPANEL_EVENTS.RADIOLOGY_REPORT_STUDY_LINKED,
  REPORT_PANEL_ADDITIONAL_TOGGLE: "REPORT_PANEL_ADDITIONAL_TOGGLE",
  REPORT_PANEL_COPY: "REPORT_PANEL_COPY",
  REPORT_PANEL_PRINT: "REPORT_PANEL_PRINT",
  REPORT_UPLOAD_PDF: MIXPANEL_EVENTS.REPORT_PDF_UPLOADED,
  REPORT_VERSION_TAB: "REPORT_VERSION_TAB",
  SETTINGS_BACK: "SETTINGS_BACK",
  SETTINGS_CLOSE: "SETTINGS_CLOSE",
  SETTINGS_NAV_COMMUNICATION_TEMPLATES: "SETTINGS_NAV_COMMUNICATION_TEMPLATES",
  SETTINGS_NAV_SMS_CONFIGURATION: "SETTINGS_NAV_SMS_CONFIGURATION",
  SETTINGS_OPEN: "SETTINGS_OPEN",
  SHARE_MODAL_CLOSE: MIXPANEL_EVENTS.PATIENT_SHARE_CLOSED,
  SHARE_COPY_LINK: MIXPANEL_EVENTS.PATIENT_SHARED,
  SHARE_NATIVE: MIXPANEL_EVENTS.PATIENT_SHARED,
  SHARE_WHATSAPP: MIXPANEL_EVENTS.PATIENT_SHARED,
  SHARE_SMS: MIXPANEL_EVENTS.PATIENT_SHARED,
  SHARE_EMAIL: MIXPANEL_EVENTS.PATIENT_SHARED,
  SHARE_INVITE: MIXPANEL_EVENTS.PATIENT_SHARED,
  SHARE_INVITE_SEARCH: MIXPANEL_EVENTS.INVITE_USERS_SEARCHED,
  SHARE_INVITE_ADD: MIXPANEL_EVENTS.INVITE_USER_ADDED,
  SHARE_REMOVE_ACCESS_OPEN: MIXPANEL_EVENTS.PATIENT_REMOVE_ACCESS_OPENED,
  SHARE_REMOVE_ACCESS_CANCEL: MIXPANEL_EVENTS.PATIENT_REMOVE_ACCESS_CANCELLED,
  SHARE_ACCESS_REVOKED: MIXPANEL_EVENTS.PATIENT_ACCESS_REVOKED,
  SMS_CONFIG_ACTIVE: "SMS_CONFIG_ACTIVE",
  SMS_CONFIG_BODY: "SMS_CONFIG_BODY",
  SMS_CONFIG_CANCEL: "SMS_CONFIG_CANCEL",
  SMS_CONFIG_LINKED_PAGE: "SMS_CONFIG_LINKED_PAGE",
  SMS_CONFIG_NAME: "SMS_CONFIG_NAME",
  SMS_CONFIG_NEW: "SMS_CONFIG_NEW",
  SMS_CONFIG_ROW: "SMS_CONFIG_ROW",
  SMS_CONFIG_SAVE: "SMS_CONFIG_SAVE",
  SMS_CONFIG_SEARCH: "SMS_CONFIG_SEARCH",
  SMS_CONFIG_TOKEN: "SMS_CONFIG_TOKEN",
  SMS_CONFIG_WORKSPACE: "SMS_CONFIG_WORKSPACE",
  SMS_PANEL_OPENED: MIXPANEL_EVENTS.SMS_PANEL_OPENED,
  SMS_TAB_COMPOSE: "SMS_TAB_COMPOSE",
  SMS_TAB_HISTORY: "SMS_TAB_HISTORY",
  SMS_TEMPLATE_SELECT: MIXPANEL_EVENTS.SMS_TEMPLATE_SELECTED,
  SMS_REPORT_SELECT: "SMS_REPORT_SELECT",
  SMS_SEND: MIXPANEL_EVENTS.SMS_SEND_INITIATED,
  SMS_SENT: MIXPANEL_EVENTS.SMS_SENT,
  SMS_SEND_FAILED: MIXPANEL_EVENTS.SMS_SEND_FAILED,
  SMS_RETRY: MIXPANEL_EVENTS.SMS_RETRY,
  SMS_COPY_LINK: "SMS_COPY_LINK",
  SMOKING_QTY: "SMOKING_QTY",
  SMOKING_TYPE: "SMOKING_TYPE",
  SMOKING_YEARS: "SMOKING_YEARS",
  STATE_VIEW_RETRY: "STATE_VIEW_RETRY",
  STUDIES_ADD_SCANS: MIXPANEL_EVENTS.ADD_SCANS_FOR_PATIENT,
  STUDIES_OPEN_VIEWER: "STUDIES_OPEN_VIEWER",
  STUDY_REPORT_AI: "STUDY_REPORT_AI",
  STUDY_REPORT_RADIOLOGY: "STUDY_REPORT_RADIOLOGY",
  STUDY_REPORT_TOGGLE: "STUDY_REPORT_TOGGLE",
  STUDY_SERIES: MIXPANEL_EVENTS.SERIES_CLICKED,
  SUGGESTED_ACTION: "SUGGESTED_ACTION",
  SUGGESTED_DECISION_CHANGE: "SUGGESTED_DECISION_CHANGE",
  SUGGESTED_DECISION_CONFIRM: "SUGGESTED_DECISION_CONFIRM",
  SUGGESTED_DECISION_OVERRIDE: "SUGGESTED_DECISION_OVERRIDE",
  TABS: "TABS",
  TAG_ACTION_APPLY: "TAG_ACTION_APPLY",
  TAG_ACTION_APPLY_ALL: "TAG_ACTION_APPLY_ALL",
  TAG_ACTION_DELETE: "TAG_ACTION_DELETE",
  TAG_ACTION_UNDO: "TAG_ACTION_UNDO",
  TAG_DELETE: MIXPANEL_EVENTS.PATIENT_TAG_DELETED,
  TAG_ITEM: "TAG_ITEM",
  TAG_REVERT: MIXPANEL_EVENTS.PATIENT_TAG_REVERTED,
  TAG_VALUE_DATE: "TAG_VALUE_DATE",
  TAG_VALUE_SELECT: "TAG_VALUE_SELECT",
  TASK_ACTION_CREATE: "TASK_ACTION_CREATE",
  TASK_ACTION_DATE: "TASK_ACTION_DATE",
  TASK_ACTION_DELETE: "TASK_ACTION_DELETE",
  TASK_ACTION_LABEL: "TASK_ACTION_LABEL",
  TASK_ACTION_OWNER: "TASK_ACTION_OWNER",
  TASK_ACTION_SELECT_PATIENT_RECORD: "TASK_ACTION_SELECT_PATIENT_RECORD",
  TASK_ACTION_TITLE: "TASK_ACTION_TITLE",
  TASK_DETAIL_ADD_COMMENT: "TASK_DETAIL_ADD_COMMENT",
  TASK_DETAIL_CLOSE: "TASK_DETAIL_CLOSE",
  TASK_DETAIL_COMMENTS_TOGGLE: "TASK_DETAIL_COMMENTS_TOGGLE",
  TASK_DETAIL_COMMENT_DISCARD: "TASK_DETAIL_COMMENT_DISCARD",
  TASK_DETAIL_COMMENT_INPUT: "TASK_DETAIL_COMMENT_INPUT",
  TASK_DETAIL_COMMENT_SAVE: "TASK_DETAIL_COMMENT_SAVE",
  TASK_DETAIL_DELETE: "TASK_DETAIL_DELETE",
  TASK_DETAIL_DELETE_CANCEL: "TASK_DETAIL_DELETE_CANCEL",
  TASK_DETAIL_DELETE_CONFIRM: "TASK_DETAIL_DELETE_CONFIRM",
  TASK_DETAIL_DESCRIPTION: "TASK_DETAIL_DESCRIPTION",
  TASK_DETAIL_DUE_DATE: "TASK_DETAIL_DUE_DATE",
  TASK_DETAIL_LABEL: "TASK_DETAIL_LABEL",
  TASK_DETAIL_LINKED_RESOURCES: "TASK_DETAIL_LINKED_RESOURCES",
  TASK_DETAIL_MARK_DONE: "TASK_DETAIL_MARK_DONE",
  TASK_DETAIL_META_DISCARD: "TASK_DETAIL_META_DISCARD",
  TASK_DETAIL_META_SAVE: "TASK_DETAIL_META_SAVE",
  TASK_DETAIL_MINIMIZE: "TASK_DETAIL_MINIMIZE",
  TASK_DETAIL_MINIMIZED_CLOSE: "TASK_DETAIL_MINIMIZED_CLOSE",
  TASK_DETAIL_MINIMIZED_RESTORE: "TASK_DETAIL_MINIMIZED_RESTORE",
  TASK_DETAIL_OWNER: "TASK_DETAIL_OWNER",
  TASK_DETAIL_SHORTCUT_1M: "TASK_DETAIL_SHORTCUT_1M",
  TASK_DETAIL_SHORTCUT_1W: "TASK_DETAIL_SHORTCUT_1W",
  TASK_DETAIL_SHORTCUT_1Y: "TASK_DETAIL_SHORTCUT_1Y",
  TASK_DETAIL_SHORTCUT_3D: "TASK_DETAIL_SHORTCUT_3D",
  TASK_DETAIL_TITLE_DISCARD: "TASK_DETAIL_TITLE_DISCARD",
  TASK_DETAIL_TITLE_EDIT: "TASK_DETAIL_TITLE_EDIT",
  TASK_DETAIL_TITLE_INPUT: "TASK_DETAIL_TITLE_INPUT",
  TASK_DETAIL_TITLE_SAVE: "TASK_DETAIL_TITLE_SAVE",
  TASK_ITEM: "TASK_ITEM",
  TASK_MODAL_CLOSE: "TASK_MODAL_CLOSE",
  TASK_MODAL_RESTORE: "TASK_MODAL_RESTORE",
  TEMPLATE_CANCEL: "TEMPLATE_CANCEL",
  TEMPLATE_NAME: "TEMPLATE_NAME",
  TEMPLATE_SAVE: "TEMPLATE_SAVE",
  TERMS_AGREE: MIXPANEL_EVENTS.TERMS_AGREED,
  TERMS_DISAGREE: MIXPANEL_EVENTS.TERMS_DISAGREED,
  TERMS_DISCLAIMER_TOGGLE: MIXPANEL_EVENTS.TERMS_CONFIRM_TOGGLED,
  TERMS_MODAL_CLOSE: MIXPANEL_EVENTS.TERMS_MODAL_CLOSED,
  TERMS_PP_LINK: MIXPANEL_EVENTS.PRIVACY_POLICY_CLICKED,
  TERMS_REJECT_CANCEL: MIXPANEL_EVENTS.TERMS_LOGOUT_CANCELLED,
  TERMS_REJECT_PROCEED: MIXPANEL_EVENTS.TERMS_LOGOUT_CONFIRMED,
  TERMS_TC_LINK: MIXPANEL_EVENTS.TERMS_LINK_CLICKED,
  TIMELINE_CREATE: "TIMELINE_CREATE",
  TIMELINE_CREATE_ATTACHMENT: MIXPANEL_EVENTS.ATTACHMENTS_POPOVER_OPENED,
  TIMELINE_DELETE: "TIMELINE_DELETE",
  TIMELINE_EDIT: "TIMELINE_EDIT",
  TIMELINE_FEED: "TIMELINE_FEED",
  TIMELINE_FILTER: "TIMELINE_FILTER",
  TIMELINE_ITEM: "TIMELINE_ITEM",
  TIMELINE_MEDIA_OPEN: "TIMELINE_MEDIA_OPEN",
  TOOLBAR_SUPPORT: "TOOLBAR_SUPPORT",
  SUPPORT_WIDGET_TICKET_CREATED: MIXPANEL_EVENTS.SUPPORT_WIDGET_TICKET_CREATED,
  SUPPORT_WIDGET_ERROR: MIXPANEL_EVENTS.SUPPORT_WIDGET_ERROR,
  SUPPORT_WIDGET_MINIMIZED: MIXPANEL_EVENTS.SUPPORT_WIDGET_MINIMIZED,
  SUPPORT_WIDGET_ATTACHMENT_PREVIEWED: MIXPANEL_EVENTS.SUPPORT_WIDGET_ATTACHMENT_PREVIEWED,
  SUPPORT_WIDGET_ATTACHMENT_DOWNLOADED: MIXPANEL_EVENTS.SUPPORT_WIDGET_ATTACHMENT_DOWNLOADED,
  TOOLBAR_THEME_TOGGLE: MIXPANEL_EVENTS.THEME_CHANGED,
  TOOLBAR_WORKLIST: "TOOLBAR_WORKLIST",
  WORKLIST_EXPAND: "WORKLIST_EXPAND",
  WORKLIST_FILTER_CLEAR: "WORKLIST_FILTER_CLEAR",
  WORKLIST_FILTER_SHOW_ALL: "WORKLIST_FILTER_SHOW_ALL",
  WORKLIST_ITEM: "WORKLIST_ITEM",
  WORKLIST_OPEN_FULL: "WORKLIST_OPEN_FULL",
  WORKLIST_SEARCH: MIXPANEL_EVENTS.PATIENT_SEARCH
};